import React from 'react';

import { useDispatch } from 'react-redux';

import { CoreHelper, ValidationHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useForm } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/$components';
import { FormHookOutput } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/$types';
import { FormHelper } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/helpers';
import { BoxRef, PopupMenuItem, PopupMenuRef, TooltipRef } from '@bcp-nextgen-dx-component-factory/design-system';
import { ActiveItem } from '@bcp-nextgen-dx-component-factory/design-system';
import { Validations } from '@constants';
import { getSavedTokenDataFromStorage } from '@helpers';
import { useBuildPhoneCodes, useReactiveForm, UseReactiveFormInputPropsOutput } from '@hooks';
import { saveCorporateDataFormInput, saveCorporateDataFormIsValid, useServiceResponse, useUserInput } from '@store';
import { Anything, CorporateData } from '@types';

interface CorporateFormResources {
	defaultInternationalDialing: string;
	onlineShop: string;
	inputOnlineShopName: string;
	tooltipOnlineShop: string;
	inputWebsite: string;
	contacts: string;
	corporate: string;
	inputCorporateEmail: string;
	inputCountry: string;
	inputCorporateContact: string;
	manager: string;
	tooltipManager: string;
	inputManagerName: string;
	inputManagerEmail: string;
	inputManagerContact: string;
	required: string;
	invalidEmail: string;
	invalidPhone: string;
}

interface CorporateFormHelperOutput {
	resources: CorporateFormResources;
	form: FormHookOutput;
	corporatePhoneFlagId: number;
	managerPhoneFlagId: number;
	phoneCodes: PopupMenuItem[];
	popupMenuCorporateCountryRef: React.RefObject<PopupMenuRef>;
	popupMenuManagerCountryRef: React.RefObject<PopupMenuRef>;
	anchorOnlineShopRef: React.RefObject<BoxRef>;
	tooltipOnlineShopRef: React.RefObject<TooltipRef>;
	anchorManagerRef: React.RefObject<BoxRef>;
	tooltipManagerRef: React.RefObject<TooltipRef>;
	maxLengthCorporateContact: number | undefined;
	maxLengthManagerContact: number | undefined;
	getInputProps: (name: string) => UseReactiveFormInputPropsOutput;
	handlePressManagerIndicative: (id: ActiveItem | undefined) => void;
	handlePressCorporateIndicative: (id: ActiveItem | undefined) => void;
	onClearOnlineShopName: () => void;
	onClearWebsite: () => void;
	onClearCorporateEmail: () => void;
	onClearCorporateContact: () => void;
	onClearManagerName: () => void;
	onClearManagerEmail: () => void;
	onClearManagerContact: () => void;
	submitForm: () => void;
	state: string | undefined;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useCorporateFormHelper = (): CorporateFormHelperOutput => {
	const t = useTranslation();
	const dispatch = useDispatch();
	const { phoneCodes, initialPhoneId, getSelectedPhone, getSelectedPhoneId } = useBuildPhoneCodes({
		withFlags: true,
	});
	const [corporatePhoneFlagId, setCorporatePhoneFlagId] = React.useState<number>(0);
	const [managerPhoneFlagId, setManagerPhoneFlagId] = React.useState<number>(0);

	const popupMenuCorporateCountryRef = React.useRef<PopupMenuRef>(null);
	const popupMenuManagerCountryRef = React.useRef<PopupMenuRef>(null);

	const anchorOnlineShopRef = React.useRef<BoxRef>(null);
	const tooltipOnlineShopRef = React.useRef<TooltipRef>(null);

	const anchorManagerRef = React.useRef<BoxRef>(null);
	const tooltipManagerRef = React.useRef<TooltipRef>(null);

	const { forms } = useUserInput();

	const { retrieveCompany } = useServiceResponse();

	const state = getSavedTokenDataFromStorage<string>('state');

	const resources: CorporateFormResources = {
		defaultInternationalDialing: t('global.defaultInternationalDialing'),
		onlineShop: t('placeholders.formCorporateData.onlineShop'),
		inputOnlineShopName: t('placeholders.formCorporateData.corporate.inputOnlineShopName'),
		tooltipOnlineShop: t('placeholders.formCorporateData.corporate.tooltipOnlineShop'),
		inputWebsite: t('placeholders.formCorporateData.corporate.inputWebsite'),
		contacts: t('placeholders.formCorporateData.contacts'),
		corporate: t('general.input.corporate'),
		inputCorporateEmail: t('placeholders.formCorporateData.contacts.inputCorporateEmail'),
		inputCountry: t('general.country'),
		inputCorporateContact: t('placeholders.formCorporateData.contacts.inputCorporateContact'),
		manager: t('placeholders.formCorporateData.contacts.manager'),
		tooltipManager: t('placeholders.formCorporateData.contacts.tooltipManager'),
		inputManagerName: t('placeholders.formCorporateData.contacts.inputManagerName'),
		inputManagerEmail: t('placeholders.formCorporateData.contacts.inputManagerEmail'),
		inputManagerContact: t('placeholders.formCorporateData.contacts.inputManagerContact'),
		required: t('general.errors.required'),
		invalidEmail: t('general.errors.invalidEmail'),
		invalidPhone: t('general.errors.invalidPhone'),
	};

	useDidMount(() => {
		popupMenuCorporateCountryRef.current?.setActiveItem(0);
		popupMenuManagerCountryRef.current?.setActiveItem(0);
		if (initialPhoneId !== undefined) {
			popupMenuCorporateCountryRef.current?.setActiveItem(initialPhoneId);
			popupMenuManagerCountryRef.current?.setActiveItem(initialPhoneId);
		}
		if (forms && forms.corporateData) {
			const corporateIndicativeId = getSelectedPhoneId(forms.corporateData.corporateInfo?.indicative as string);
			const responsibleIndicativeId = getSelectedPhoneId(
				forms.corporateData.responsibleInfo?.indicative as string
			);
			if (corporateIndicativeId !== -1) {
				popupMenuCorporateCountryRef.current?.setActiveItem(corporateIndicativeId);
			}
			if (responsibleIndicativeId !== -1) {
				popupMenuManagerCountryRef.current?.setActiveItem(responsibleIndicativeId);
			}
		}
		return () => {
			setCorporatePhoneFlagId(0);
			setManagerPhoneFlagId(0);
		};
	});

	const saveInputOnStore = (values: Anything) => {
		const data: CorporateData = {
			website: values.website,
			storeName: values.onlineShopName,
			corporateInfo: {
				email: values.corporateEmail,
				indicative: values.corporateIndicative,
				phone: values.corporateContact,
			},
			responsibleInfo: {
				email: values.managerEmail,
				indicative: values.managerIndicative,
				phone: values.managerContact,
				name: values.managerName,
			},
		};
		dispatch(saveCorporateDataFormInput(data));
	};

	const maxLengthCorporateContact = React.useMemo(() => {
		if (phoneCodes[corporatePhoneFlagId]?.content === resources.defaultInternationalDialing) {
			return 9;
		} else {
			return undefined;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [corporatePhoneFlagId]);

	const maxLengthManagerContact = React.useMemo(() => {
		if (phoneCodes[managerPhoneFlagId]?.content === resources.defaultInternationalDialing) {
			return 9;
		} else {
			return undefined;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [managerPhoneFlagId]);

	const clearInput = (inputProps: UseReactiveFormInputPropsOutput) => {
		inputProps.onChangeValue('');
		inputProps.onBlur(true);
	};

	const onClearOnlineShopName = () => clearInput(getInputProps('onlineShopName'));
	const onClearWebsite = () => clearInput(getInputProps('website'));
	const onClearCorporateEmail = () => clearInput(getInputProps('corporateEmail'));
	const onClearCorporateContact = () => clearInput(getInputProps('corporateContact'));
	const onClearManagerName = () => clearInput(getInputProps('managerName'));
	const onClearManagerEmail = () => clearInput(getInputProps('managerEmail'));
	const onClearManagerContact = () => clearInput(getInputProps('managerContact'));

	const setInitialValues = () => {
		if (forms && forms.corporateData) {
			return {
				website: forms.corporateData.website,
				onlineShopName: forms.corporateData.storeName,
				corporateEmail: forms.corporateData.corporateInfo?.email
					? forms.corporateData.corporateInfo?.email
					: retrieveCompany?.email,
				corporateIndicative: forms.corporateData.corporateInfo?.indicative
					? forms.corporateData.corporateInfo?.indicative
					: phoneCodes[corporatePhoneFlagId]?.content,
				corporateContact: forms.corporateData.corporateInfo?.phone,
				managerEmail: forms.corporateData.responsibleInfo?.email
					? forms.corporateData.responsibleInfo?.email
					: retrieveCompany?.email,
				managerIndicative: forms.corporateData.responsibleInfo?.indicative
					? forms.corporateData.responsibleInfo?.indicative
					: phoneCodes[managerPhoneFlagId]?.content,
				managerContact: forms.corporateData.responsibleInfo?.phone,
				managerName: forms.corporateData.responsibleInfo?.name
					? forms.corporateData.responsibleInfo?.name
					: retrieveCompany?.responsibleName,
			};
		}
		return {
			corporateEmail: retrieveCompany?.email,
			corporateIndicative: phoneCodes[corporatePhoneFlagId]?.content,
			managerName: retrieveCompany?.responsibleName,
			managerEmail: retrieveCompany?.responsibleEmail,
			managerIndicative: phoneCodes[managerPhoneFlagId]?.content,
		};
	};

	const form = useForm('corporateData');
	const { isValid, getInputProps, handleOnChangeField, submitForm } = useReactiveForm({
		name: form.name,
		initialValues: setInitialValues(),
		onSubmit: saveInputOnStore,
		validate: (values: Anything) => {
			const isCorporateContactCountryPT = values.corporateIndicative === resources.defaultInternationalDialing;
			const isManagerContactPT = values.managerIndicative === resources.defaultInternationalDialing;

			return {
				onlineShopName: FormHelper.validateField(
					values.onlineShopName,
					CoreHelper.isNullOrEmpty(state) ? [ValidationHelper.validateRequired(resources.required)] : []
				),
				website: FormHelper.validateField(
					values.website,
					CoreHelper.isNullOrEmpty(state) ? [ValidationHelper.validateRequired(resources.required)] : []
				),
				corporateEmail: FormHelper.validateField(values.corporateEmail, [
					ValidationHelper.validateRequired(resources.required),
					ValidationHelper.validateRegex(Validations.emailValidRegex, resources.invalidEmail),
				]),
				corporateContact: FormHelper.validateField(values.corporateContact, [
					ValidationHelper.validateRequired(resources.required),
					isCorporateContactCountryPT
						? ValidationHelper.validateEqualLength(9, resources.invalidPhone)
						: () => undefined,
				]),
				managerName: FormHelper.validateField(values.managerName, [
					ValidationHelper.validateRequired(resources.required),
				]),
				managerEmail: FormHelper.validateField(values.managerEmail, [
					ValidationHelper.validateRequired(resources.required),
					ValidationHelper.validateRegex(Validations.emailValidRegex, resources.invalidEmail),
				]),
				managerContact: FormHelper.validateField(values.managerContact, [
					ValidationHelper.validateRequired(resources.required),
					isManagerContactPT
						? ValidationHelper.validateEqualLength(9, resources.invalidPhone)
						: () => undefined,
				]),
			};
		},
	});

	React.useEffect(() => {
		dispatch(saveCorporateDataFormIsValid({ isValid }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isValid]);

	const handlePressManagerIndicative = (item: ActiveItem | undefined) => {
		if (item?.content !== undefined) {
			updateValueForm(item.content, 'managerIndicative');
			setManagerPhoneFlagId(item.id);
		}
		popupMenuManagerCountryRef.current?.hide();
	};

	const handlePressCorporateIndicative = (item: ActiveItem | undefined) => {
		if (item?.content !== undefined) {
			updateValueForm(item.content, 'corporateIndicative');
			setCorporatePhoneFlagId(item.id);
		}
		popupMenuCorporateCountryRef.current?.hide();
	};

	function updateValueForm(phone: string, indicative: string) {
		const selectedPhone = getSelectedPhone(phone);
		if (selectedPhone !== undefined && selectedPhone.internationalCode !== undefined) {
			handleOnChangeField(indicative)(selectedPhone.internationalCode as string);
		}
	}

	return {
		resources,
		form,
		corporatePhoneFlagId,
		managerPhoneFlagId,
		phoneCodes,
		popupMenuCorporateCountryRef,
		popupMenuManagerCountryRef,
		anchorOnlineShopRef,
		tooltipOnlineShopRef,
		anchorManagerRef,
		tooltipManagerRef,
		maxLengthCorporateContact,
		maxLengthManagerContact,
		getInputProps,
		handlePressManagerIndicative,
		handlePressCorporateIndicative,
		onClearOnlineShopName,
		onClearWebsite,
		onClearCorporateEmail,
		onClearCorporateContact,
		onClearManagerName,
		onClearManagerEmail,
		onClearManagerContact,
		submitForm,
		state,
	};
};
